import {
  CircleStackIcon,
  ClipboardDocumentIcon,
  Cog6ToothIcon,
  ComputerDesktopIcon,
  ServerStackIcon,
  Square3Stack3DIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { ComponentType, SVGProps, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  TechnologyDeployment,
  listTechnologyDeployments,
} from '../../services/technologyDeploymentsApi';
import {
  TechnologyOverviewApplication,
  TechnologyOverviewHosting,
} from '../../services/technologyApi';
import { formatDateTimeBr } from '../../utils/formatDateBr';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import { resolveStatusTone } from '../../utils/statusBadge';
import type { StatusBadgeTone } from '../../utils/StatusBadgeTone';
import { TechnologyApplicationHostingModal } from './TechnologyApplicationHostingModal';
import {
  TECHNOLOGY_APPLICATION_STATUS_OPTIONS,
  TECHNOLOGY_DEPLOYMENT_STATUS_OPTIONS,
  TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
  technologyOptionLabel,
} from './technologyFormOptions';

const COMPONENT_ICONS: ComponentType<SVGProps<SVGSVGElement>>[] = [
  Cog6ToothIcon,
  ComputerDesktopIcon,
  Square3Stack3DIcon,
  CircleStackIcon,
];

const ENVIRONMENT_DOT: Record<string, string> = {
  Production: 'bg-emerald-500',
  Homologation: 'bg-sky-500',
  Development: 'bg-amber-500',
  DisasterRecovery: 'bg-violet-500',
};

type TechnologyComponentDetailPanelProps = {
  application: TechnologyOverviewApplication;
  compositionName: string;
  iconIndex: number;
  onClose: () => void;
  onHostingChanged?: () => void;
};

function environmentOrder(kind: string): number {
  const index = TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.findIndex((option) => option.value === kind);
  return index < 0 ? TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.length : index;
}

function deploymentMoment(deployment: TechnologyDeployment): number {
  const value = deployment.finishedAtUtc || deployment.executedAtUtc || deployment.scheduledAtUtc;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function latestDeployment(
  deployments: TechnologyDeployment[],
  environmentKind: string,
): TechnologyDeployment | undefined {
  return deployments
    .filter((deployment) => deployment.environmentKind === environmentKind)
    .sort((left, right) => deploymentMoment(right) - deploymentMoment(left))[0];
}

function publicationAt(deployment: TechnologyDeployment | undefined): string | null | undefined {
  return deployment?.finishedAtUtc || deployment?.executedAtUtc;
}

function deploymentTone(status: string): StatusBadgeTone {
  switch (status) {
    case 'Succeeded':
      return 'success';
    case 'InProgress':
      return 'info';
    case 'Failed':
    case 'Cancelled':
    case 'RolledBack':
      return 'danger';
    default:
      return resolveStatusTone(status);
  }
}

function copyText(value: string): void {
  void navigator.clipboard.writeText(value).then(
    () => toast.success('Link copiado.'),
    () => toast.error('Não foi possível copiar o link.'),
  );
}

function DetailRow({ label, children }: { label: string; children: string }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-secondary-100 py-3 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-sm text-secondary-500">{label}</dt>
      <dd className="min-w-0 whitespace-pre-wrap break-words text-sm text-secondary-900">{children}</dd>
    </div>
  );
}

export function TechnologyComponentDetailPanel({
  application,
  compositionName,
  iconIndex,
  onClose,
  onHostingChanged,
}: TechnologyComponentDetailPanelProps) {
  const { canReadModule, canWriteModule } = usePermissions();
  const canReadHosting = canReadModule('technology.applications');
  const canWriteHosting = canWriteModule('technology.applications');
  const [hostingOpen, setHostingOpen] = useState(false);
  const [deployments, setDeployments] = useState<TechnologyDeployment[]>([]);
  const [deploymentsLoading, setDeploymentsLoading] = useState(true);
  const Icon = COMPONENT_ICONS[iconIndex % COMPONENT_ICONS.length] ?? Cog6ToothIcon;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape' && !hostingOpen) onClose();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, hostingOpen]);

  useEffect(() => {
    setHostingOpen(false);
  }, [application.id]);

  useEffect(() => {
    let cancelled = false;
    setDeploymentsLoading(true);
    void listTechnologyDeployments({ applicationId: application.id, page: 1, pageSize: 50 })
      .then((result) => {
        if (!cancelled) setDeployments(result.items);
      })
      .catch((error) => {
        if (!cancelled) {
          setDeployments([]);
          toast.error(getRequestErrorMessage(error, 'Falha ao carregar as implantações.'));
        }
      })
      .finally(() => {
        if (!cancelled) setDeploymentsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [application.id]);

  const hostings = [...application.hostings].sort(
    (left, right) =>
      environmentOrder(left.environmentKind) - environmentOrder(right.environmentKind)
      || left.infrastructureAssetName.localeCompare(right.infrastructureAssetName, 'pt-BR')
      || (left.address ?? '').localeCompare(right.address ?? '', 'pt-BR'),
  );
  const languages = application.languages.filter((language) => language.trim().length > 0).join(', ');
  const repository = application.repositoryUrl?.trim() ?? '';

  return (
    <>
    <aside className="flex max-h-[70vh] flex-col overflow-hidden rounded-2xl border border-secondary-200 bg-white shadow-sm xl:max-h-[calc(100vh-2rem)]">
      <div className="border-b border-secondary-100 p-4">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-sky-50 text-sky-600">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="min-w-0 truncate text-base font-semibold text-secondary-900">
                {application.name}
              </h3>
              <div className="flex shrink-0 items-center gap-1.5">
                <StatusBadge tone={resolveStatusTone(application.status)}>
                  {technologyOptionLabel(TECHNOLOGY_APPLICATION_STATUS_OPTIONS, application.status)}
                </StatusBadge>
                <button
                  type="button"
                  onClick={onClose}
                  aria-label="Fechar"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-secondary-500 transition-colors hover:bg-secondary-100 hover:text-secondary-800"
                >
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
            </div>
            <p className="mt-0.5 text-xs text-secondary-500">
              Componente da aplicação {compositionName}
            </p>
          </div>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <dl>
          <DetailRow label="Descrição">{application.notes?.trim() || '—'}</DetailRow>
          <DetailRow label="Linguagem">{languages || '—'}</DetailRow>
          <DetailRow label="Versão atual">{application.version?.trim() || '—'}</DetailRow>
          <div className="grid grid-cols-1 gap-1 py-3 sm:grid-cols-[8.5rem_minmax(0,1fr)] sm:items-center sm:gap-3">
            <dt className="text-sm text-secondary-500">Repositório</dt>
            <dd className="flex min-w-0 items-center gap-1 text-sm text-secondary-900">
              {repository ? (
                <>
                  <a
                    href={repository}
                    target="_blank"
                    rel="noreferrer"
                    title={repository}
                    className="min-w-0 truncate text-sky-700 hover:underline"
                  >
                    {repository}
                  </a>
                  <button
                    type="button"
                    aria-label="Copiar repositório"
                    title="Copiar repositório"
                    onClick={() => copyText(repository)}
                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
                  </button>
                </>
              ) : (
                '—'
              )}
            </dd>
          </div>
        </dl>

        <HostingSection
          hostings={hostings}
          deployments={deployments}
          deploymentsLoading={deploymentsLoading}
          onConfigure={canReadHosting ? () => setHostingOpen(true) : undefined}
        />
      </div>
    </aside>
    {hostingOpen ? (
      <TechnologyApplicationHostingModal
        open
        application={application}
        canWrite={canWriteHosting}
        onClose={() => setHostingOpen(false)}
        onChanged={onHostingChanged}
      />
    ) : null}
    </>
  );
}

function HostingSection({
  hostings,
  deployments,
  deploymentsLoading,
  onConfigure,
}: {
  hostings: TechnologyOverviewHosting[];
  deployments: TechnologyDeployment[];
  deploymentsLoading: boolean;
  onConfigure?: () => void;
}) {
  return (
    <section className="mt-5">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-secondary-900">
          Hospedagens ({hostings.length})
        </h4>
        {onConfigure ? (
          <button
            type="button"
            onClick={onConfigure}
            className="inline-flex shrink-0 items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-sky-700 transition-colors hover:bg-sky-50"
          >
            <ServerStackIcon className="h-4 w-4" aria-hidden="true" />
            Configurar
          </button>
        ) : null}
      </div>
      {hostings.length === 0 ? (
        <p className="mt-3 text-sm text-secondary-500">Nenhuma hospedagem cadastrada.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {hostings.map((hosting) => {
            const deployment = latestDeployment(deployments, hosting.environmentKind);
            const publishedAt = publicationAt(deployment);
            const serverLabel = hosting.infrastructureVendor
              ? `${hosting.infrastructureAssetName} (${hosting.infrastructureVendor})`
              : hosting.infrastructureAssetName;
            return (
              <li
                key={hosting.id}
                className="rounded-xl border border-secondary-200 p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="inline-flex min-w-0 items-center gap-2 text-sm font-medium text-secondary-900">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-full ${ENVIRONMENT_DOT[hosting.environmentKind] ?? 'bg-secondary-400'}`}
                    />
                    <span className="truncate">
                      {technologyOptionLabel(
                        TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS,
                        hosting.environmentKind,
                      )}
                    </span>
                  </span>
                  {deployment ? (
                    <StatusBadge tone={deploymentTone(deployment.status)}>
                      {technologyOptionLabel(TECHNOLOGY_DEPLOYMENT_STATUS_OPTIONS, deployment.status)}
                    </StatusBadge>
                  ) : null}
                </div>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-2">
                    <dt className="text-secondary-500">Servidor</dt>
                    <dd className="flex min-w-0 items-start gap-1.5 text-secondary-900">
                      <ServerStackIcon className="mt-0.5 h-4 w-4 shrink-0 text-secondary-400" aria-hidden="true" />
                      <span className="min-w-0 break-words">{serverLabel}</span>
                    </dd>
                  </div>
                  <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-2">
                    <dt className="text-secondary-500">Endereço</dt>
                    <dd className="min-w-0 break-all text-secondary-900" title={hosting.address?.trim() || undefined}>
                      {hosting.address?.trim() || '—'}
                    </dd>
                  </div>
                  <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-2">
                    <dt className="text-secondary-500">Última publicação</dt>
                    <dd className="text-secondary-900">
                      {deploymentsLoading ? 'Carregando...' : formatDateTimeBr(publishedAt)}
                    </dd>
                  </div>
                  <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-2">
                    <dt className="text-secondary-500">Versão</dt>
                    <dd className="text-secondary-900">
                      {deploymentsLoading ? 'Carregando...' : deployment?.releaseVersion?.trim() || '—'}
                    </dd>
                  </div>
                </dl>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
